import { Command } from "commander";
import { readFileSync, writeFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { confirm } from "@inquirer/prompts";
import { fatalError } from "../ux/errors.js";

export const renameCommand = new Command("rename")
  .description("Rename the project in all .fathom/ config files")
  .argument("<name>", "New project name")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(async (name: string, options: { yes?: boolean }) => {
   try {
    const baseDir = process.cwd();
    const fathomDir = resolve(baseDir, ".fathom");

    if (!existsSync(fathomDir)) {
      fatalError("Project not initialized", ".fathom/ directory not found", "Run fathom init first");
    }

    const jsonFiles = [
      resolve(fathomDir, "config.json"),
      resolve(fathomDir, "registry.json"),
      resolve(fathomDir, "tracking", "summary.json"),
    ];

    // Read old name from config for confirmation prompt
    let oldName = "unnamed";
    const configPath = resolve(fathomDir, "config.json");
    if (existsSync(configPath)) {
      try {
        const configData = JSON.parse(readFileSync(configPath, "utf-8"));
        if (configData.project) oldName = configData.project;
      } catch {
        // will be caught below
      }
    }

    // Confirmation prompt (skip with --yes)
    if (!options.yes) {
      const proceed = await confirm({
        message: `Rename project "${oldName}" to "${name}"?`,
        default: true,
      });
      if (!proceed) {
        console.log(chalk.dim("\n  Aborted."));
        return;
      }
    }

    let updated = 0;

    for (const filePath of jsonFiles) {
      if (!existsSync(filePath)) continue;

      let data: { project?: string; [key: string]: unknown };
      try {
        data = JSON.parse(readFileSync(filePath, "utf-8"));
      } catch {
        fatalError("Invalid config", "Failed to parse " + filePath, "Delete the file and re-run fathom init");
      }
      if (data.project) {
        oldName = data.project;
        data.project = name;
        writeFileSync(filePath, JSON.stringify(data, null, 2) + "\n");
        updated++;
      }
    }

    // Update SKILL.md
    const skillPath = resolve(baseDir, ".claude", "skills", "fathom", "SKILL.md");
    if (existsSync(skillPath)) {
      const content = readFileSync(skillPath, "utf-8");
      const newContent = content.replaceAll(oldName, name);
      if (newContent !== content) {
        writeFileSync(skillPath, newContent);
        updated++;
      }
    }

    // Update hook script
    const hookPath = resolve(baseDir, ".claude", "hooks", "te-session-sync.sh");
    if (existsSync(hookPath)) {
      const content = readFileSync(hookPath, "utf-8");
      const newContent = content.replaceAll(oldName, name);
      if (newContent !== content) {
        writeFileSync(hookPath, newContent);
        updated++;
      }
    }

    console.log(
      chalk.bold(`\n  Renamed "${oldName}" → "${name}" (${updated} files updated)\n`),
    );
   } catch (err: unknown) {
     if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
       console.log(chalk.dim("\n  Exiting.\n"));
       return;
     }
     throw err;
   }
  });
